import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';
import { ACCESS, REFRESH } from 'constants/cuboh';

import * as authHelper from 'helpers/auth.cuboh';
import { createCoreAxiosInstance } from 'createAxiosInstance';

function setAccessToken(accessToken) {
  window.localStorage.setItem(ACCESS, accessToken);
}

function setRefreshToken(refreshToken) {
  window.localStorage.setItem(REFRESH, refreshToken);
}

function setTokens(access, refresh) {
  setAccessToken(access);
  setRefreshToken(refresh);
}

/**
 * A reusable hook for managing a set of access tokens
 *
 * @param {refreshOnPageLoad} bool: Boolean to perform a refresh on the hook mounting
 * @param {keepTokenAlive} bool: Boolean on whether to mount a timer to auto-refresh the token
 * @param {onLogin} function: Callback for when tokens are set via login function
 * @param {onLogout} function: Callback for when tokens are cleared / token fails to refresh
 */

const DEFAULT_PARAMS = {
  refreshOnPageLoad: false,
  keepTokenAlive: false,
};

export default function useAuthToken(props = DEFAULT_PARAMS) {
  const [cnAccessToken, setCnAccessToken] = useState(authHelper.getAccessToken());

  const { refreshOnPageLoad, keepTokenAlive } = {
    ...DEFAULT_PARAMS,
    ...props,
  };
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const clearTokens = () => {
    [ACCESS, REFRESH].forEach(key => window.localStorage.removeItem(key));
  };

  /** Perform a token refresh */
  const refreshUsersToken = async () => {
    try {
      const refreshToken = authHelper.getRefreshToken();

      if (!refreshToken) {
        return;
      }
      // check for cuboh_refresh only, if user closes browser they will lose cuboh_access token
      if (authHelper.tokenIsIssuedByChowNow(refreshToken)) {
        const { accessToken } = await authHelper.getLatestTokens();
        setCnAccessToken(accessToken);
      } else {
        const axiosInstance = await createCoreAxiosInstance();
        const { data } = await axiosInstance.post('api/v2/token/refresh', {
          refresh: refreshToken,
        });
        setAccessToken(data.access);
      }
    } catch (error) {
      Sentry.captureException(error);
      clearTokens();
    }
  };

  useEffect(() => {
    if (!refreshOnPageLoad) return;
    refreshUsersToken();
  }, [refreshOnPageLoad, refreshUsersToken]);

  useEffect(() => {
    if (!keepTokenAlive) return;

    // Refresh every 10 minutes
    const timer = setTimeout(
      async () => {
        await refreshUsersToken();
        setLastRefreshed(new Date());
      },
      10 * 60 * 1000,
    );
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [lastRefreshed, refreshUsersToken, keepTokenAlive]);

  return {
    cnAccessToken,
    clearTokens,
    setTokens,
  };
}
